/**
 * Shared types, labels, and formatting helpers for Pattern of Life report
 * components (PolReportViewer, PolReportDashboard, ExportReportDialog).
 *
 * Kept free of React imports so it can be consumed anywhere without cycles.
 */

import { PolAnalysisType } from '@signals/app-shell/types';

// ---------------------------------------------------------------------------
// Payload types (mirror PolReportService output)
// ---------------------------------------------------------------------------

export interface PolEntity {
  entity_id: string;
  entity_type?: string;
  routine_score: number;
  mobility_score: number;
  social_score: number;
  anomaly_score: number;
  total_dwells: number;
  unique_locations: number;
  total_dwell_duration_minutes: number;
  total_routes: number;
  total_distance_km: number;
  total_calls: number;
  total_sms: number;
  unique_contacts: number;
  anomaly_count: number;
  ai_summary?: string | null;
  top_locations?: Array<Record<string, any>>;
  active_hours?: any[];
  active_days?: any[];
  days_with_data?: number;
  data_confidence?: number | null;
  baseline_established?: boolean | null;
}

export interface PolReportPayload {
  generated_at: string;
  course_id?: string;
  entities_requested: string[];
  date_range: { start: string; end: string };
  analysis_types: PolAnalysisType[];
  entities: PolEntity[];
  sections: Partial<Record<PolAnalysisType, any[]>>;
  section_counts: Partial<Record<PolAnalysisType, number>>;
  warnings: string[];
}

// ---------------------------------------------------------------------------
// Display metadata
// ---------------------------------------------------------------------------

export const ANOMALY_TYPE_LABELS: Record<string, string> = {
  ml_physical_anomaly: 'ML Physical Anomaly',
  ml_digital_anomaly: 'ML Digital Anomaly',
  ml_anomaly: 'ML Anomaly',
  routine_break: 'Routine Break',
  unusual_location: 'Unusual Location',
  time_shift: 'Time Shift',
  dwell_duration_anomaly: 'Dwell Duration Anomaly',
  lapse_break: 'Lapse Break',
  route_deviation: 'Route Deviation',
  new_relationship: 'New Relationship',
  new_pattern: 'New Pattern',
  unusual_time: 'Unusual Time',
  high_activity: 'High Activity',
  low_activity: 'Low Activity',
  no_activity: 'No Activity',
  new_location: 'New Location',
};

export const SEVERITY_COLORS: Record<string, 'default' | 'info' | 'warning' | 'error'> = {
  low: 'info',
  medium: 'warning',
  high: 'error',
  critical: 'error',
};

/** Ordinal weight so severities sort correctly (critical first). */
export const SEVERITY_RANK: Record<string, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

export const formatDurationSeconds = (seconds?: number | null): string => {
  if (!seconds || seconds <= 0) return '—';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

export const formatDistanceMeters = (meters?: number | null): string => {
  if (!meters || meters <= 0) return '—';
  const miles = meters / 1609.344;
  return miles >= 0.1 ? `${miles.toFixed(1)} mi` : `${Math.round(meters)} m`;
};

export const formatDay = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleDateString() : '—';

export const formatDateTime = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleString() : '—';

export const formatHour = (hour?: number | null): string => {
  if (hour == null || hour < 0) return '—';
  const h = hour % 24;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display} ${suffix}`;
};

/** Day-of-week normalization: accepts names ("Monday"/"mon") or numbers. */
export const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const normalizeDayIndex = (value: any): number | null => {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    // Python weekday convention: 0 = Monday.
    const idx = Math.round(value);
    return idx >= 0 && idx <= 6 ? idx : null;
  }
  const s = String(value).trim().toLowerCase().slice(0, 3);
  const idx = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].indexOf(s);
  return idx >= 0 ? idx : null;
};
